<script lang="ts">
	import BulletList from "@components/common/BulletList.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import InfoTable from "@components/common/InfoTable.svelte";
	import Layout from "@components/layout/Layout.svelte";
	import { email } from "../components/email.js";
	import { getGlobalSettings } from "../contexts/getGlobalSettings.js";
	import { getTranslate } from "../utils/getTranslate.js";
	import translations from "./PrivacyPolicy.translations.json" with { type: "json" };
	import type { PrivacyPolicyProps } from "./PrivacyPolicyProps.js";

	const _: PrivacyPolicyProps = $props();
	const translate = getTranslate(translations);
	const { COOKIES } = getGlobalSettings();

	const contactRows = [
		{ label: translate("companyNameAndSurname"), value: translate("companyName") },
		{ label: translate("addressLabel"), value: translate("address") },
		{ label: translate("companyIdLabel"), value: translate("companyId") },
		{ label: translate("phoneLabel"), value: translate("phone") },
		{ label: translate("emailLabel"), value: email, isEmail: true, emailHref: `mailto:${email}` },
	];

	const purposeItems = [
		{ text: translate("purpose1") },
		{ text: translate("purpose2") },
		{ text: translate("purpose3") },
		{ text: translate("purpose4") },
		{ text: translate("purpose5") },
	];
</script>

<Layout>
	<!-- Hlavní nadpis -->
	<section class="full-width grid-container py-8 sm:py-10 md:py-12 lg:py-[5.5rem]">
		<div class="container-center">
			<h1 class="text-zms-blue">
				<Formatted text={translate("pageTitle")} />
			</h1>
		</div>
	</section>

	<!-- Úvod -->
	<section class="full-width grid-container py-8 md:py-12">
		<div class="container-center">
			<h2 class="text-zms-blue-dark mb-3 md:mb-6">
				<Formatted text={translate("securityTitle")} />
			</h2>
			<p class="text-optical-black text-base md:text-lg leading-relaxed">
				<Formatted text={translate("securityDescription")} />
			</p>
		</div>
	</section>

	<!-- Totožnost správce -->
	<section class="full-width grid-container py-8 md:py-12 bg-zms-grey">
		<div class="container-center">
			<h2 class="text-zms-blue-dark mb-3 md:mb-6">
				<Formatted text={translate("identityTitle")} />
			</h2>
			<InfoTable rows={contactRows} />
		</div>
	</section>

	<!-- Typ shromažďovaných údajů -->
	<section class="full-width grid-container py-8 md:py-12">
		<div class="container-center">
			<h2 class="text-zms-blue-dark mb-3 md:mb-6">
				<Formatted text={translate("dataTypeTitle")} />
			</h2>
			<p class="text-optical-black text-base md:text-lg leading-relaxed">
				<Formatted text={translate("dataTypeDescription")} />
			</p>
		</div>
	</section>

	<!-- Účel zpracování -->
	<section class="full-width grid-container py-8 md:py-12 bg-zms-grey">
		<div class="container-center">
			<h2 class="text-zms-blue-dark mb-3 md:mb-6">
				<Formatted text={translate("purposeTitle")} />
			</h2>
			<p class="text-optical-black text-base md:text-lg leading-relaxed mb-4 md:mb-6">
				<Formatted text={translate("purposeDescription")} />
			</p>
			<BulletList items={purposeItems} bulletColor="bg-zms-blue" spacing="space-y-2" />
		</div>
	</section>

	<!-- Sdílení s třetími stranami -->
	<section class="full-width grid-container py-8 md:py-12">
		<div class="container-center">
			<h2 class="text-zms-blue-dark mb-3 md:mb-6">
				<Formatted text={translate("sharingTitle")} />
			</h2>
			<p class="text-optical-black text-base md:text-lg leading-relaxed mb-6 md:mb-8">
				<Formatted text={translate("sharingDescription")} />
			</p>

			<!-- Tabulka cookies -->
			<div class="bg-zms-grey rounded-2xl p-4 md:p-6 lg:p-8 mb-6 md:mb-8 overflow-x-auto">
				<script
					type="text/javascript"
					charset="UTF-8"
					data-cookiescriptreport="report"
					src="https://report.cookie-script.com/r/{COOKIES}.js"
				></script>
			</div>

			<p class="text-optical-black leading-relaxed text-base md:text-lg">
				<Formatted text={translate("thirdPartyInfo")} />
			</p>
		</div>
	</section>

	<!-- Cookies -->
	<section class="full-width grid-container py-8 md:py-12 bg-zms-grey">
		<div class="container-center">
			<h2 class="text-zms-blue-dark mb-3 md:mb-6">
				<Formatted text={translate("cookiesTitle")} />
			</h2>
			<h3 class="text-zms-blue-dark text-lg md:text-xl font-semibold mb-4">
				<Formatted text={translate("cookiesAboutTitle")} />
			</h3>
			<div class="space-y-4 md:space-y-6">
				<p class="text-optical-black leading-relaxed text-base md:text-lg">
					<Formatted text={translate("cookiesDescription")} />
				</p>
				<p class="text-optical-black leading-relaxed text-base md:text-lg">
					<Formatted text={translate("cookiesPersonalizationInfo")} />
				</p>

				<div class="grid grid-cols-1 md:grid-cols-2 gap-4">
					<div class="bg-white rounded-xl p-4 md:p-5">
						<h4 class="font-semibold text-zms-blue-dark mb-2 text-base md:text-lg">
							<Formatted text={translate("essentialCookiesTitle")} />
						</h4>
						<p class="text-optical-black text-sm md:text-base leading-relaxed">
							<Formatted text={translate("essentialCookiesDescription")} />
						</p>
					</div>
					<div class="bg-white rounded-xl p-4 md:p-5">
						<h4 class="font-semibold text-zms-blue-dark mb-2 text-base md:text-lg">
							<Formatted text={translate("performanceCookiesTitle")} />
						</h4>
						<p class="text-optical-black text-sm md:text-base leading-relaxed">
							<Formatted text={translate("performanceCookiesDescription")} />
						</p>
					</div>
					<div class="bg-white rounded-xl p-4 md:p-5">
						<h4 class="font-semibold text-zms-blue-dark mb-2 text-base md:text-lg">
							<Formatted text={translate("targetingCookiesTitle")} />
						</h4>
						<p class="text-optical-black text-sm md:text-base leading-relaxed">
							<Formatted text={translate("targetingCookiesDescription")} />
						</p>
					</div>
					<div class="bg-white rounded-xl p-4 md:p-5">
						<h4 class="font-semibold text-zms-blue-dark mb-2 text-base md:text-lg">
							<Formatted text={translate("functionalCookiesTitle")} />
						</h4>
						<p class="text-optical-black text-sm md:text-base leading-relaxed">
							<Formatted text={translate("functionalCookiesDescription")} />
						</p>
					</div>
					<div class="bg-white rounded-xl p-4 md:p-5 md:col-span-2">
						<h4 class="font-semibold text-zms-blue-dark mb-2 text-base md:text-lg">
							<Formatted text={translate("cookieManagementTitle")} />
						</h4>
						<p class="text-optical-black text-sm md:text-base leading-relaxed">
							<Formatted text={translate("cookieManagementDescription")} />
						</p>
					</div>
				</div>
			</div>
		</div>
	</section>

	<!-- Bezpečnostní postupy -->
	<section class="full-width grid-container py-8 md:py-12">
		<div class="container-center">
			<h2 class="text-zms-blue-dark mb-3 md:mb-6">
				<Formatted text={translate("securityPracticesTitle")} />
			</h2>
			<p class="text-optical-black text-base md:text-lg leading-relaxed">
				<Formatted text={translate("securityPracticesDescription")} />
			</p>
		</div>
	</section>

	<!-- Kontrola osobních údajů -->
	<section class="full-width grid-container py-8 md:py-12 bg-zms-grey">
		<div class="container-center">
			<h2 class="text-zms-blue-dark mb-3 md:mb-6">
				<Formatted text={translate("controlTitle")} />
			</h2>
			<p class="text-optical-black text-base md:text-lg leading-relaxed">
				<Formatted text={translate("controlDescription")} />
			</p>
		</div>
	</section>

	<!-- Obecná ustanovení -->
	<section class="full-width grid-container py-8 md:py-12">
		<div class="container-center">
			<h2 class="text-zms-blue-dark mb-3 md:mb-6">
				<Formatted text={translate("generalProvisionsTitle")} />
			</h2>
			<div class="space-y-3 md:space-y-6 text-optical-black leading-relaxed text-base md:text-lg">
				<p class="space-y-3 md:space-y-4 text-optical-black leading-relaxed text-base md:text-lg">
					<Formatted text={translate("generalProvision1")} />
				</p>
				<p class="space-y-3 md:space-y-4 text-optical-black leading-relaxed text-base md:text-lg">
					<Formatted text={translate("generalProvision2")} />
				</p>
				<p class="space-y-3 md:space-y-4 text-optical-black leading-relaxed text-base md:text-lg">
					<Formatted text={translate("generalProvision3")} />
				</p>
				<BulletList
					items={[{ text: translate("generalProvisionItem1") }, { text: translate("generalProvisionItem2") }]}
					bulletColor="bg-zms-blue"
					spacing="space-y-2"
				/>
				<p class="space-y-3 md:space-y-4 text-optical-black leading-relaxed text-base md:text-lg">
					<Formatted text={translate("generalProvision4")} />
				</p>
				<p class="space-y-3 md:space-y-4 text-optical-black leading-relaxed text-base md:text-lg">
					<Formatted text={translate("generalProvision5")} />
				</p>
				<p class="space-y-3 md:space-y-4 text-optical-black leading-relaxed text-base md:text-lg">
					<Formatted text={translate("generalProvision6")} />
				</p>
			</div>
		</div>
	</section>
</Layout>
